export type WorkflowMediaPreviewType = 'iframe' | 'html' | 'image' | 'pdf';

export type WorkflowMediaPreview = {
  type: WorkflowMediaPreviewType;
  src?: string;
  srcDoc?: string;
};

export type WorkflowMediaPreviewInput = {
  embedString?: string | null;
  fileUrl?: string | null;
  fileType?: string | null;
  fileName?: string | null;
  fileKey?: string | null;
};

export type WorkflowMediaPresenceInput = {
  fileKey?: string | null;
  embedString?: string | null;
};

export type WorkflowMediaDisplayItem = WorkflowMediaPresenceInput & {
  name?: string;
};

export function hasWorkflowMediaContent({ fileKey, embedString }: WorkflowMediaPresenceInput): boolean {
  return Boolean(fileKey || embedString?.trim());
}

export function classifyWorkflowMediaPreview({
  embedString,
  fileUrl,
  fileType,
  fileName,
  fileKey,
}: WorkflowMediaPreviewInput): WorkflowMediaPreview | null {
  const trimmedEmbedString = embedString?.trim();
  if (trimmedEmbedString) {
    return classifyEmbedString(trimmedEmbedString);
  }

  const trimmedFileUrl = fileUrl?.trim();
  if (!trimmedFileUrl) {
    return null;
  }

  if (isHtmlFile(trimmedFileUrl, fileType, fileName, fileKey)) {
    return {
      type: 'iframe',
      src: trimmedFileUrl,
    };
  }

  if (isPdfFile(trimmedFileUrl, fileType)) {
    return {
      type: 'pdf',
      src: trimmedFileUrl,
    };
  }

  return {
    type: 'image',
    src: trimmedFileUrl,
  };
}

function classifyEmbedString(embedString: string): WorkflowMediaPreview {
  if (isIframe(embedString)) {
    const src = extractSrcFromIframe(embedString);
    if (src) {
      return {
        type: 'iframe',
        src,
      };
    }
  }

  if (isUrl(embedString)) {
    return {
      type: 'iframe',
      src: embedString,
    };
  }

  return {
    type: 'html',
    srcDoc: embedString,
  };
}

function isIframe(content: string): boolean {
  return /<iframe\b[^>]*>/i.test(content);
}

function extractSrcFromIframe(iframeHtml: string): string | null {
  const srcMatch = iframeHtml.match(/src\s*=\s*["']([^"']+)["']/i);
  return srcMatch ? srcMatch[1] : null;
}

function isUrl(content: string): boolean {
  try {
    new URL(content);
    return true;
  } catch {
    return false;
  }
}

function isPdfFile(url: string, fileType?: string | null): boolean {
  return fileType?.toLowerCase() === 'application/pdf'
    || url.toLowerCase().endsWith('.pdf')
    || url.toLowerCase().includes('.pdf?');
}

function isHtmlFile(
  url: string,
  fileType?: string | null,
  fileName?: string | null,
  fileKey?: string | null,
): boolean {
  if (fileType?.toLowerCase() === 'text/html') {
    return true;
  }

  return hasHtmlExtension(url)
    || hasHtmlExtension(fileName)
    || hasHtmlExtension(fileKey);
}

function hasHtmlExtension(value?: string | null): boolean {
  if (!value) {
    return false;
  }

  const normalizedValue = value.toLowerCase();
  return normalizedValue.endsWith('.html') || normalizedValue.includes('.html?');
}
